import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import IconButton, { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import { eventMessageCopyUserMessage } from '../../../../ga4/ga4';

// TD-1731: Custom link renderer for external links (same as AgentAIMessage)
// Opens links in new tab for better UX
const LinkRenderer = ({ href, children }) => {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

// Fallback copy method for browsers without the async clipboard API
const fallbackCopyToClipboard = (text) => {
  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      // execCommand('copy') is the intentional legacy fallback used only when the
      // async Clipboard API is unavailable (tried first in handleCopyToClipboard).
      const successful = document.execCommand('copy'); // NOSONAR - deprecated API kept as fallback
      textArea.remove();
      if (successful) {
        resolve();
      } else {
        reject(new Error('Fallback copy failed'));
      }
    } catch (err) {
      textArea.remove();
      reject(err);
    }
  });
};

// TD-2345 Copy / TD-2346 Rerun: message-level controls on user messages.
// - Copy: copies the exact raw text the user sent (no rendered-HTML artefacts,
//   no "You:" prefix) so a prompt can be pasted and adapted for a sibling search.
// - Rerun (when onRerun is provided): resubmits the message as a new turn. While a
//   task is running the control is disabled with a "Stop the current task first"
//   tooltip (TD-2346, Q2). The "Rerun of …" subtitle is supplied by the parent.
const AgentUserMessage = observer(({ content, subtitle = null, onRerun = null, disabled = false }) => {
  const [copyState, setCopyState] = useState({ status: 'idle', tooltip: 'Copy to clipboard' });

  // Reset the transient "Copied!"/"Copy failed" tooltip after a delay; the cleanup
  // prevents a setState on an unmounted component if the bubble scrolls out mid-toast.
  useEffect(() => {
    if (copyState.status !== 'success' && copyState.status !== 'error') {
      return undefined;
    }
    const resetMs = copyState.status === 'error' ? 3000 : 2000;
    const timerId = setTimeout(
      () => setCopyState({ status: 'idle', tooltip: 'Copy to clipboard' }),
      resetMs
    );
    return () => clearTimeout(timerId);
  }, [copyState.status]);

  const handleCopyToClipboard = async () => {
    const text = content ?? '';
    try {
      setCopyState({ status: 'copying', tooltip: 'Copying...' });

      if (navigator.clipboard && globalThis.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        await fallbackCopyToClipboard(text);
      }

      setCopyState({ status: 'success', tooltip: 'Copied!' });
      eventMessageCopyUserMessage();
    } catch (error) {
      console.error('Failed to copy user message to clipboard:', error);
      setCopyState({ status: 'error', tooltip: 'Copy failed' });
    }
  };

  const rerunTooltip = disabled ? 'Stop the current task first' : 'Rerun this message';

  return (
    <div className="agent-user-message">
      <div className="agent-user-message-header">
        <span className="agent-user-label">You</span>
      </div>
      <div className="agent-message-bubble agent-user-bubble">
        <div className="agent-message-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
      {subtitle && <div className="agent-user-message-subtitle">{subtitle}</div>}
      <div className="agent-user-message-actions">
        <IconButton
          icon={ICON_BUTTON_ICONS.COPY}
          tooltipText={copyState.tooltip}
          tooltipPosition="bottom"
          withFill={false}
          withBorder={false}
          onClick={handleCopyToClipboard}
          disabled={copyState.status === 'copying'}
        />
        {onRerun && (
          <IconButton
            icon={ICON_BUTTON_ICONS.ARROW_REPEAT}
            tooltipText={rerunTooltip}
            tooltipPosition="bottom"
            withFill={false}
            withBorder={false}
            onClick={onRerun}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
});

export default AgentUserMessage;
